import * as React from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { Button } from "@material-ui/core";
import ReactMarkdown from "react-markdown";
import { Link } from "gatsby";
import Section from "../Section";
import {
  SECTION_TINT,
  SECTION_WHITE,
  SECTION_NAVY,
  TEXT_BODY,
  TEXT_HEADING,
  SECTION_PADDING_Y,
} from "../../styles/homepageSections";

export type HeadlineWithCtaVariant = "tint" | "white" | "navy";

export type HeadlineWithCtaData = {
  headline: string;
  subheadline?: string | null;
  cta: {
    text: string;
    link: string;
  };
  variant?: HeadlineWithCtaVariant | string | null;
};

const markdownComponents = {
  p: ({ children }: { children?: React.ReactNode }) => <p>{children}</p>,
  strong: ({ children }: { children?: React.ReactNode }) => <strong>{children}</strong>,
};

const useStyles = makeStyles((t: Theme) =>
  createStyles({
    section: {
      padding: SECTION_PADDING_Y.mobile,
      [t.breakpoints.up("md")]: {
        padding: SECTION_PADDING_Y.desktop,
      },
    },
    inner: {
      maxWidth: 900,
      margin: "0 auto",
      textAlign: "center",
    },
    headline: {
      fontSize: 32,
      fontWeight: 600,
      lineHeight: 1.34,
      "& p": {
        margin: 0,
      },
      "& strong": {
        color: t.palette.primary.main,
        fontWeight: 600,
      },
      [t.breakpoints.up("md")]: {
        fontSize: 44,
        lineHeight: 1.43,
      },
    },
    headlineLight: {
      color: TEXT_HEADING,
    },
    headlineDark: {
      color: "#fff",
      "& strong": {
        color: t.palette.primary.main,
      },
    },
    subheadline: {
      fontSize: 18,
      fontWeight: 400,
      lineHeight: 1.5,
      marginTop: 20,
      maxWidth: 720,
      marginLeft: "auto",
      marginRight: "auto",
      "& p": {
        margin: 0,
      },
      "& strong": {
        fontWeight: 600,
      },
      [t.breakpoints.up("md")]: {
        fontSize: 20,
        lineHeight: 1.6,
        marginTop: 24,
      },
    },
    subheadlineLight: {
      color: TEXT_BODY,
      "& strong": {
        color: TEXT_HEADING,
      },
    },
    subheadlineDark: {
      color: "rgba(255, 255, 255, 0.88)",
      "& strong": {
        color: "#fff",
      },
    },
    ctaWrapper: {
      marginTop: 40,
      display: "flex",
      justifyContent: "center",
      [t.breakpoints.down("xs")]: {
        marginTop: 32,
      },
    },
    ctaLink: {
      textDecoration: "none",
      display: "inline-flex",
      width: "100%",
      maxWidth: 320,
      [t.breakpoints.up("sm")]: {
        width: "auto",
        maxWidth: "none",
      },
    },
    ctaButton: {
      width: "100%",
      minHeight: 56,
      padding: "14px 40px",
      fontSize: 18,
      fontWeight: 600,
      textTransform: "none",
      color: "#fff",
      backgroundColor: t.palette.primary.main,
      borderRadius: 0,
      boxShadow: "none",
      "&:hover": {
        backgroundColor: t.palette.primary.main,
        opacity: 0.9,
        boxShadow: "none",
      },
      [t.breakpoints.up("sm")]: {
        width: "auto",
        minWidth: 220,
      },
    },
  })
);

const isExternalLink = (href: string) =>
  href.startsWith("http://") || href.startsWith("https://") || href.startsWith("mailto:");

const hasMarkdownContent = (value?: string | null) =>
  Boolean(value && value.replace(/<[^>]*>/g, "").trim());

const normalizeVariant = (value?: string | null): HeadlineWithCtaVariant => {
  if (value === "white" || value === "navy" || value === "tint") {
    return value;
  }
  return "tint";
};

const sectionColor = (variant: HeadlineWithCtaVariant) => {
  switch (variant) {
    case "navy":
      return SECTION_NAVY;
    case "white":
      return SECTION_WHITE;
    default:
      return SECTION_TINT;
  }
};

const HeadlineWithCta: React.FC<HeadlineWithCtaData> = ({
  headline,
  subheadline,
  cta,
  variant: variantProp,
}) => {
  const classes = useStyles();
  const variant = normalizeVariant(variantProp);
  const isDark = variant === "navy";
  const showSubheadline = hasMarkdownContent(subheadline);

  if (!headline || !cta?.text || !cta?.link) {
    return null;
  }

  const button = (
    <Button variant="contained" className={classes.ctaButton} disableElevation>
      {cta.text}
    </Button>
  );

  return (
    <Section color={sectionColor(variant)} className={classes.section}>
      <div className={classes.inner}>
        <div
          className={[
            classes.headline,
            isDark ? classes.headlineDark : classes.headlineLight,
          ].join(" ")}
        >
          <ReactMarkdown linkTarget="_blank" components={markdownComponents}>
            {headline}
          </ReactMarkdown>
        </div>

        {showSubheadline && (
          <div
            className={[
              classes.subheadline,
              isDark ? classes.subheadlineDark : classes.subheadlineLight,
            ].join(" ")}
          >
            <ReactMarkdown linkTarget="_blank" components={markdownComponents}>
              {subheadline!}
            </ReactMarkdown>
          </div>
        )}

        <div className={classes.ctaWrapper}>
          {isExternalLink(cta.link) ? (
            <a
              href={cta.link}
              className={classes.ctaLink}
              target={cta.link.startsWith("mailto:") ? undefined : "_blank"}
              rel="noopener noreferrer"
            >
              {button}
            </a>
          ) : (
            <Link to={cta.link} className={classes.ctaLink}>
              {button}
            </Link>
          )}
        </div>
      </div>
    </Section>
  );
};

export default HeadlineWithCta;
